import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Colors } from '../../theme';
import { TONYTE_PROFILE_IMAGE } from '../../utils/userProfiles';
import VibeVerifiedBadge from '../VibeVerifiedBadge';

const METALLIC_RING_COLORS = [
  'rgba(255,255,255,0.5)',
  'rgba(214,196,168,0.22)',
  'rgba(255,214,170,0.3)',
  'rgba(255,255,255,0.45)',
];

interface Props {
  uri?: string;
  size?: number;
  isOnline?: boolean;
  isVerified?: boolean;
}

/**
 * Premium avatar: thin metallic gradient ring always, an animated soft green
 * pulse ring layered on top when online, and a small verified badge. Static
 * gradients + one native-driver opacity loop (only while online) — cheap.
 */
const ChatAvatar: React.FC<Props> = ({ uri, size = 48, isOnline, isVerified }) => {
  const [failed, setFailed] = useState(false);
  const pulseAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    setFailed(false);
  }, [uri]);

  useEffect(() => {
    if (!isOnline) return undefined;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1, duration: 1400, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 0, duration: 1400, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [isOnline, pulseAnim]);

  const imageSize = size - 6;

  return (
    <View style={{ width: size, height: size }}>
      <LinearGradient
        colors={METALLIC_RING_COLORS}
        start={{ x: 0.1, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={[styles.ring, { width: size, height: size, borderRadius: size / 2, padding: 1.6 }]}
      >
        <View style={[styles.inner, { borderRadius: (size - 3.2) / 2 }]}>
          <Image
            source={uri && !failed ? { uri } : TONYTE_PROFILE_IMAGE}
            style={{ width: imageSize, height: imageSize, borderRadius: imageSize / 2 }}
            resizeMode="cover"
            onError={() => setFailed(true)}
          />
        </View>
      </LinearGradient>

      {isOnline ? (
        <>
          <Animated.View
            pointerEvents="none"
            style={[
              styles.onlinePulse,
              {
                width: size + 8,
                height: size + 8,
                borderRadius: (size + 8) / 2,
                left: -4,
                top: -4,
                opacity: pulseAnim.interpolate({ inputRange: [0, 1], outputRange: [0.55, 0.15] }),
                transform: [{ scale: pulseAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] }) }],
              },
            ]}
          />
          <View style={[styles.onlineDot, { right: 1, bottom: 1 }]} />
        </>
      ) : null}

      <VibeVerifiedBadge verified={isVerified} size={size} />
    </View>
  );
};

export default ChatAvatar;

const styles = StyleSheet.create({
  ring: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  inner: {
    overflow: 'hidden',
    backgroundColor: '#09101A',
  },
  onlinePulse: {
    position: 'absolute',
    borderWidth: 1.5,
    borderColor: Colors.success,
  },
  onlineDot: {
    position: 'absolute',
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Colors.success,
    borderWidth: 2,
    borderColor: '#050812',
  },
});
